/**
 * DevSimulatorPanel
 *
 * Pannello popup con opzioni di simulazione per il form corrente.
 * Mostra opzioni specifiche per il form attivo e genera dati casuali
 * ma validi secondo le regole di validazione del form.
 *
 * @author Salvo de Luca - Dialogicom s.r.l.s.
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { Beaker, Shuffle, X, Check, Building2, User, FileText, FileCheck, Sparkles, Keyboard, Power } from 'lucide-react';
import { useDevSimulator } from './DevSimulatorContext';

export const DevSimulatorPanel: React.FC = () => {
  const {
    isEnabled,
    isPanelOpen,
    setPanelOpen,
    registeredForms,
    activeFormId,
    simulateActiveForm,
    autoStartEnabled,
    setAutoStartEnabled,
    toggleEnabled
  } = useDevSimulator();

  // Recupera il form attivo (può essere null)
  const activeForm = activeFormId ? registeredForms.get(activeFormId) : null;

  // Stato opzioni selezionate
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

  // Effetto typing (opzione globale del pannello)
  const [typingEffect, setTypingEffect] = useState(false);

  // Calcola un hash delle opzioni per rilevare cambiamenti nei defaultValue
  const optionsHash = activeForm?.options
    ?.map(opt => `${opt.id}:${opt.defaultValue}`)
    .join('|') || '';

  // Inizializza opzioni con valori default (DEVE essere prima dei return condizionali!)
  // Si aggiorna anche quando cambiano i defaultValue (es. sedeCorrente dall'header)
  useEffect(() => {
    if (!activeForm) return;

    const defaults: Record<string, string> = {};
    activeForm.options?.forEach(opt => {
      defaults[opt.id] = opt.defaultValue;
    });
    setSelectedOptions(defaults);
  }, [activeForm?.id, optionsHash]);

  // Non mostrare se disabilitato, chiuso, nessun form, o form non trovato
  if (!isEnabled || !isPanelOpen || registeredForms.size === 0 || !activeForm) {
    return null;
  }

  const handleOptionChange = (optionId: string, value: string) => {
    setSelectedOptions(prev => ({ ...prev, [optionId]: value }));
  };

  const handleSimulate = () => {
    // Passa le opzioni del form + l'opzione globale typingEffect
    simulateActiveForm({
      ...selectedOptions,
      _typingEffect: typingEffect ? 'true' : 'false'
    });
  };

  // Render icona per opzione
  const getOptionIcon = (optionId: string, value: string) => {
    if (optionId === 'customerType') {
      return value === 'company' ? <Building2 className="w-4 h-4" /> : <User className="w-4 h-4" />;
    }
    if (optionId === 'fillMode') {
      return value === 'required' ? <FileCheck className="w-4 h-4" /> : <FileText className="w-4 h-4" />;
    }
    return null;
  };

  return (
    <div
      className="fixed bottom-20 left-6 z-[9999] w-80 max-w-[calc(100vw-48px)]"
      role="dialog"
      aria-label="Simulatore Dati"
      onMouseDown={(e) => {
        // Previeni il blur degli input del form quando si interagisce col pannello
        e.preventDefault();
      }}
    >
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-amber-500/30 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Beaker className="w-5 h-5 text-white" />
            <div>
              <h3 className="font-bold text-white text-sm">DEV SIMULATOR</h3>
              <p className="text-amber-100 text-xs opacity-80">{activeForm.displayName}</p>
            </div>
          </div>
          <button
            onClick={() => setPanelOpen(false)}
            className="text-white/70 hover:text-white p-1 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning Badge */}
        <div className="px-4 pt-3">
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-3 py-2 flex items-center gap-2">
            <span className="text-red-400 text-xs font-semibold">
              ⚠️ SOLO PER TEST - Disabilitare in produzione
            </span>
          </div>
        </div>

        {/* Opzioni */}
        {activeForm.options && activeForm.options.length > 0 && (
          <div className="p-4 space-y-3">
            {activeForm.options.map((option) => (
              <div key={option.id}>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">
                  {option.label}
                </label>

                {option.type === 'select' && option.values && (
                  <div className="grid grid-cols-2 gap-2">
                    {option.values.map((val) => {
                      const isSelected = selectedOptions[option.id] === val.value;
                      return (
                        <button
                          key={val.value}
                          type="button"
                          onClick={() => handleOptionChange(option.id, val.value)}
                          className={`
                            flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
                            border-2 transition-all duration-200
                            ${isSelected
                              ? 'bg-amber-500/20 border-amber-500 text-amber-200'
                              : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500'
                            }
                          `}
                        >
                          {getOptionIcon(option.id, val.value)}
                          <span>{val.label}</span>
                          {isSelected && <Check className="w-3 h-3" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {option.type === 'toggle' && (
                  <button
                    type="button"
                    onClick={() => handleOptionChange(option.id, selectedOptions[option.id] === 'true' ? 'false' : 'true')}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium
                      border-2 transition-all duration-200
                      ${selectedOptions[option.id] === 'true'
                        ? 'bg-green-500/20 border-green-500 text-green-200'
                        : 'bg-slate-700/50 border-slate-600 text-slate-300'
                      }
                    `}
                  >
                    <span>{option.label}</span>
                    <div className={`w-8 h-4 rounded-full transition-colors ${selectedOptions[option.id] === 'true' ? 'bg-green-500' : 'bg-slate-600'}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${selectedOptions[option.id] === 'true' ? 'translate-x-4' : 'translate-x-0'}`} />
                    </div>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Description */}
        {activeForm.description && (
          <div className="px-4 pb-2">
            <p className="text-xs text-gray-500">{activeForm.description}</p>
          </div>
        )}

        {/* Preview hint */}
        <div className="px-4 pb-3">
          <div className="bg-slate-700/50 rounded-lg p-2.5">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>Dati casuali ma <strong className="text-amber-300">validi</strong> (CF, P.IVA, email...)</span>
            </div>
          </div>
        </div>

        {/* Typing Effect Toggle */}
        <div className="px-4 pb-3">
          <button
            type="button"
            onClick={() => setTypingEffect(!typingEffect)}
            className={`
              w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-medium
              border-2 transition-all duration-200
              ${typingEffect
                ? 'bg-purple-500/20 border-purple-500 text-purple-200'
                : 'bg-slate-700/50 border-slate-600 text-slate-300 hover:border-slate-500'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <Keyboard className="w-4 h-4" />
              <span>Effetto Digitazione</span>
            </div>
            <div className={`w-10 h-5 rounded-full transition-colors relative ${typingEffect ? 'bg-purple-500' : 'bg-slate-600'}`}>
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${typingEffect ? 'left-5' : 'left-0.5'}`} />
            </div>
          </button>
        </div>

        {/* Actions */}
        <div className="p-4 pt-0">
          <button
            type="button"
            onClick={handleSimulate}
            className="
              w-full flex items-center justify-center gap-2
              px-4 py-3 rounded-xl
              bg-gradient-to-r from-amber-500 to-orange-500
              hover:from-amber-400 hover:to-orange-400
              text-white font-bold text-sm
              shadow-lg shadow-amber-500/30
              transition-all duration-200
              hover:scale-[1.02] active:scale-[0.98]
            "
          >
            <Shuffle className="w-5 h-5" />
            <span>Genera Dati Casuali</span>
          </button>
        </div>

        {/* Configurazione */}
        <div className="px-4 pb-3 space-y-2">
          {/* Auto-start switch */}
          <button
            type="button"
            onClick={() => setAutoStartEnabled(!autoStartEnabled)}
            className={`
              w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium
              border transition-all duration-200
              ${autoStartEnabled
                ? 'bg-green-500/10 border-green-500/30 text-green-300'
                : 'bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500'
              }
            `}
          >
            <div className="flex items-center gap-2">
              <Power className="w-3.5 h-3.5" />
              <span>Avvia abilitato all'apertura</span>
            </div>
            <div className={`w-8 h-4 rounded-full transition-colors relative ${autoStartEnabled ? 'bg-green-500' : 'bg-slate-600'}`}>
              <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${autoStartEnabled ? 'left-4' : 'left-0.5'}`} />
            </div>
          </button>

          {/* Disabilita simulatore */}
          <button
            type="button"
            onClick={() => {
              setPanelOpen(false);
              toggleEnabled();
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
              bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-all"
          >
            <Power className="w-3.5 h-3.5" />
            <span>Disabilita Simulatore</span>
          </button>
        </div>

        {/* Footer info */}
        <div className="px-4 pb-3 text-center">
          <p className="text-[10px] text-slate-500">
            Form: <span className="text-slate-400">{activeForm.id}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DevSimulatorPanel;
